<template>
  <div class="lesson_exit">
    <div class="exit_btn">
      <a type="primary" icon="el-icon-close" size="large" @click="showMethod">{{ leaveButton }}</a>
    </div>

    <el-dialog :visible.sync="closeDialog" center custom-class="exit-dialog" :show-close="false">
      <span slot="title">{{ $t(`leaveMyStageConfirmation.dialogTitle.${accType}`) }}</span>
      <p class="text-center description">
        <span v-html="dialogMessage"></span>
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeDialog = false" class="exit-buttons exit-button-no">
          {{ $t('leaveMyStageConfirmation.controls.cancel') }}
        </el-button>
        <el-button type="primary" @click="closeCurrentTab" class="exit-buttons exit-button-yes">
          {{ $t('leaveMyStageConfirmation.controls.confirm') }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-if="trialLogout"
      :visible.sync="logoutConfirm"
      center
      custom-class="exit-dialog"
      :show-close="false"
    >
      <span slot="title">{{ $t('triallesson.logout.title') }}</span>
      <p class="text-center description">
        <span>{{ $t('triallesson.logout.message') }}</span>
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="logoutConfirm = false" class="exit-buttons exit-button-no">
          {{ $t('triallesson.logout.cancel') }}
        </el-button>
        <el-button type="primary" @click="logout" class="exit-buttons exit-button-yes">
          {{ $t('triallesson.logout.title') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { lessonStatus } from '@/utils/constants';
import { store } from '@/utils/common';
import mainStore from '@/store/index';
import logger from '@/modules/Logger';
import gracePeriodMixin from '@/mixins/gracePeriod';
import userLocalTimezoneMixin from '@/mixins/userLocalTimezone';

export default {
  mixins: [gracePeriodMixin, userLocalTimezoneMixin],
  props: {
    trialLogout: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      closeDialog: false,
      logoutConfirm: false,
      dialogMessage: null
    };
  },
  computed: {
    ...mapState(['programId', 'lessonStart', 'lessonEnd', 'accType', 'isTrial']),
    ...mapGetters(['isStudent', 'isLessonTest']),
    ...mapState('socketio', ['lessonResultCode']),
    ...mapState('triallesson', ['pageUrl']),
    ...mapState('lesson', ['isLessonWithinGracePeriod', 'isLessonHasEnded']),

    leaveButton() {
      return this.trialLogout
        ? this.$t('triallesson.exit')
        : this.$t('leaveMyStageConfirmation.controls.leave');
    }
  },
  mounted(){
    window.addEventListener("beforeunload", this.submitExitLog);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.submitExitLog);
  },
  methods: {
    ...mapMutations('lesson', ['setIsLessonHasEnded']),
    ...mapMutations('modal', ['setIsStudentCloseModal']),
    showMethod() {
      return this.trialLogout ? this.trialLessonLogout() : this.showDialogMessage();
    },
    sleeperFunction(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    submitExitLog(){
      logger.debug('User has disconnected.', { 
        operation: 'socket:connect', 
        event_type: 83, 
        lesson_id: mainStore.state.lessonId 
      });
    },
    closeCurrentTab() {
      this.closeDialog = false;
      
      const wasUserAccessMyStageUsingLessonUrl = document.referrer === '';
      
      
      if (this.isStudent) {
        if (this.isLessonHasEnded || !this.isLessonWithinGracePeriod) {
          return wasUserAccessMyStageUsingLessonUrl ? history.back() : window.close();
        } else {
          this.setIsStudentCloseModal(true);
          this.setIsLessonHasEnded(true);
        }
      } else {
        return wasUserAccessMyStageUsingLessonUrl ? history.back() : window.close();
      }
      
    },
    showDialogMessage() {
      if (this.isLessonTest || this.isTrial) {
        this.showTestCallMessage();
        return;
      }

      this.closeDialog = true;

      let message = '';
      if (this.lessonStatus === lessonStatus.scheduled) {
        const local = this.toLocal(this.lessonStart);
        const formatted = `${local.date} (${local.dow}) ${local.time}`;
        message = this.$t('leaveMyStageConfirmation.beforeLessonStart', [formatted]);
      } else if (this.lessonStatus === lessonStatus.ongoing && this.isWithinLessonTime()) {
        const localEnd = this.toLocal(this.lessonEnd);
        const formattedEnd = `${localEnd.date} (${localEnd.dow}) ${localEnd.time}`;
        message = this.$t('leaveMyStageConfirmation.duringLessonTime', [formattedEnd]);
      } else if (this.lessonStatus === lessonStatus.ongoing && this.isWithinGracePeriod()) {
        message = this.$t(`leaveMyStageConfirmation.duringGracePeriod.${this.accType}`);
      } else {
        message = this.$t(`leaveMyStageConfirmation.afterGracePeriod.${this.accType}`);
      }

      this.dialogMessage = message;
    },
    showTestCallMessage() {
      this.$notify({
        title: 'Notice',
        message: this.$t('leaveMyStageConfirmation.testCallLeave'),
        type: 'warning',
        position: 'top-left',
        duration: 15000
      });
    },
    trialLessonLogout() {
      this.logoutConfirm = true;
    },
    logout() {
      const user = JSON.parse(store.getLessonGeneratorCredentials(this.pageUrl));
      this.submitExitLog()
      this.sleeperFunction(2000);
      if (user !== null) {
        store.clearLessonGeneratorCredentials(this.pageUrl);
        this.logoutConfirm = false;
      }

      location.reload();
    }
  },
  watch: {
    lessonResultCode(result) {
      if (result !== 0 && result !== 1) {
        if (this.$te(`exitMessage.${result}`)) {
          this.dialogMessage = this.$t(`exitMessage.${result}`);
        } else {
          this.dialogMessage = this.$t('exitMessage.unknown', { re: result });
        }
      }
    }
  }
};
</script>

<style scoped src="@/assets/css/pc/exit.css"></style>

<style>
@media screen and (min-width: 960px) {
  .exit-dialog {
    float: right;
    top: 30px;
    right: 30px;
    margin: 0 !important;
    width: 380px;
  }

  .exit-dialog .el-dialog__footer {
    padding-bottom: 43px;
  }
}

@media screen and (max-width: 959px) {
  .exit-dialog {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin-top: 0 !important;
    width: 89%;
    height: 260px;
  }
}

@media screen and (max-width: 375px) {
  .exit-dialog {
    height: 265px;
  }

  .dialog-footer .exit-buttons {
    display: block;
    margin: 0 auto;
  }

  .dialog-footer .exit-buttons:last-child {
    margin-top: 4px !important;
  }
}
</style>
